/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { Button, INodeProps, ITelemetryContent, getPayloadObject, getTelemetryAttributes } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { InputID, InputType } from '../cip-business-sign-up';
import { ICipBusinessSignUpResources } from '../cip-business-sign-up.props.autogenerated';

export interface IBusinessSignUpInputProps {
    id?: string;
    type: InputType;
    value: string;
    pattern?: string;
    className: string;
    maxLength?: string;
    isRequired: boolean;
    fieldReference?: React.RefObject<HTMLInputElement>;
    onChange?(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void;
    onClick?: () => void;
    onConfirmCode?: (code: string) => void;
    telemetryContent: ITelemetryContent;
    buttonClassName?: string;
    isEmailVerificationInProcess?: boolean;
    isEmailVerified?: boolean;
    isUserVerificationFailed?: boolean;
    emailValidationCode: string;
    resources: ICipBusinessSignUpResources;
    isSubmittedPressed: boolean;
    isUserExisting: boolean;
    hasExistingProspect: boolean;
    url: string;
}

export interface IBusinessSignUpInput {
    key: string;
    AddressItem: INodeProps;
    label: React.ReactNode;
    alert: React.ReactNode;
    input: React.ReactNode;
}

const GetMaxLength = (maxLength?: string): number | undefined => {
    if (maxLength) {
        const parsedMaxLength = Number.parseInt(maxLength, 10);
        if (!isNaN(parsedMaxLength)) {
            return parsedMaxLength;
        }
    }
    return undefined;
};

export const SignUpEmailVerification: React.FC<IBusinessSignUpInputProps> = ({
    id,
    value,
    pattern,
    className,
    maxLength,
    isRequired,
    onChange,
    fieldReference,
    onClick,
    telemetryContent,
    buttonClassName,
    onConfirmCode,
    isEmailVerificationInProcess,
    isEmailVerified,
    isUserVerificationFailed,
    emailValidationCode,
    resources,
    isSubmittedPressed,
    isUserExisting,
    hasExistingProspect,
    url
}) => {
    const payLoad = getPayloadObject('click', telemetryContent, resources.sendEmailVerificationLabel);
    const attributes = getTelemetryAttributes(telemetryContent, payLoad);
    const testEmail = (email: string) => {
        // eslint-disable-next-line security/detect-unsafe-regex
        const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(email);
    };

    return (
        <React.Fragment>
            <div className='row'>
                <div className='col-12 col-md-6'>
                    <input
                        id={id}
                        type={'email'}
                        value={value}
                        className={`${className}-input ${value === '' && isSubmittedPressed && 'necessary'}`}
                        pattern={pattern !== '' ? pattern : undefined}
                        aria-required={isRequired}
                        maxLength={GetMaxLength(maxLength)}
                        onChange={onChange}
                        ref={fieldReference}
                    />
                </div>
                <div className='col-12 col-md-6'>
                    {onClick && (
                        <Button
                            id={id}
                            className={buttonClassName ? `${buttonClassName}` : ''}
                            disabled={isEmailVerificationInProcess || !testEmail(value)}
                            onClick={() => onClick()}
                            role='button'
                            {...attributes}
                        >
                            {resources.sendEmailVerificationLabel}
                        </Button>
                    )}
                </div>
            </div>
            {isEmailVerificationInProcess &&
                onConfirmCode &&
                (!isEmailVerified ? (
                    <div className='cip-business-sign-up__form-item'>
                        <label htmlFor={InputID.EmailValidation}>{resources.verifyEmailCodeLabel}</label>
                        <div className='row'>
                            <div className='col-12 col-md-6'>
                                <input
                                    id={InputID.EmailValidation}
                                    type='text'
                                    value={emailValidationCode}
                                    className={`${className}-input`}
                                    pattern={pattern !== '' ? pattern : undefined}
                                    aria-required={isRequired}
                                    maxLength={GetMaxLength(maxLength)}
                                    onChange={onChange}
                                    ref={fieldReference}
                                />
                            </div>
                            <div className='col-12 col-md-6'>
                                <Button
                                    // disabled={isEmailVerificationInProcess}
                                    className={buttonClassName ? `${buttonClassName}` : ''}
                                    onClick={() => onConfirmCode(emailValidationCode)}
                                    role='button'
                                    disabled={emailValidationCode === ''}
                                    {...attributes}
                                >
                                    {resources.confirmEmailCodeButtonLabel}
                                </Button>
                            </div>
                        </div>
                        <div className={'msc-alert msc-alert-warning'} role='alert'>
                            {resources.emailCodeHasBeenSent}
                        </div>
                        {isUserVerificationFailed && (
                            <div className={'msc-alert msc-alert-danger'}>
                                {isUserExisting
                                    ? `${resources.isUserExisting}  `
                                    : hasExistingProspect
                                    ? resources.verificationPending
                                    : resources.emailVerificationFailed}
                                {isUserExisting && <a href={url}>{resources.signInLinkText}</a>}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className={'msc-alert msc-alert-success'} role='alert'>
                        {resources.emailVerified}
                    </div>
                ))}
        </React.Fragment>
    );
};

export default SignUpEmailVerification;
